import { Box, Typography, Button, Grid, Chip, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { Check, Star, Lock } from '@mui/icons-material'
import { COLORS, TIER_COLORS } from '../constants/colors'
import { TEXT } from '../constants/text'
import { TIERS, TIER_CONFIG } from '../constants/tiers'
import { useTier } from '../hooks/useTier'

const goldPulse = keyframes`
  0%, 100% { box-shadow: 0 0 10px ${COLORS.gold.glow}; }
  50%      { box-shadow: 0 0 30px ${COLORS.gold.glow}, 0 0 60px rgba(255,215,0,0.15); }
`

const TierCard = styled(Box)(({ featured }) => ({
  padding: '32px 24px',
  borderRadius: 16,
  border: `2px solid ${featured ? COLORS.gold.border : COLORS.border.light}`,
  background: featured
    ? `linear-gradient(180deg, ${COLORS.gold.muted} 0%, ${COLORS.bg.card} 100%)`
    : COLORS.bg.glass,
  textAlign: 'center',
  position: 'relative',
  transition: 'all .3s',
  ...(featured && {
    animation: `${goldPulse} 4s ease-in-out infinite`,
  }),
  '&:hover': {
    transform: 'translateY(-6px)',
    borderColor: featured ? COLORS.gold.primary : COLORS.border.medium,
  },
}))

const tiers = [
  { key: TIERS.FREE, featured: false },
  { key: TIERS.PRO, featured: false },
  { key: TIERS.ELITE, featured: true },
]

export default function PricingPage() {
  const { tier: currentTier, setTier } = useTier()

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 900, mb: 1, color: COLORS.gold.primary }}
        >
          Choose Your Edge
        </Typography>
        <Typography sx={{ color: COLORS.text.secondary, maxWidth: 500, mx: 'auto' }}>
          Non-custodial signal intelligence. Pick the tier that fits your trading.
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {tiers.map(({ key, featured }) => {
          const cfg = TIER_CONFIG[key]
          const tc = TIER_COLORS[key]
          const isCurrent = currentTier === key

          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <TierCard featured={featured ? 1 : 0}>
                {featured && (
                  <Chip
                    label="MOST POPULAR"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 800,
                      fontSize: '0.6rem',
                      letterSpacing: '1px',
                      background: COLORS.gold.primary,
                      color: '#000',
                    }}
                  />
                )}

                {/* Icon */}
                <Box sx={{ fontSize: 36, mb: 1 }}>{cfg.icon}</Box>

                {/* Name */}
                <Typography variant="h5" sx={{ fontWeight: 800, color: tc.primary, mb: 0.5 }}>
                  {cfg.name}
                </Typography>

                <Typography sx={{ fontSize: '0.75rem', color: COLORS.text.muted, letterSpacing: '1px', textTransform: 'uppercase', mb: 2 }}>
                  {cfg.label}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 3 }}>
                  <Typography component="span" sx={{ fontWeight: 900, fontSize: '2.5rem', color: COLORS.text.primary }}>
                    ${cfg.price}
                  </Typography>
                  {cfg.price > 0 && (
                    <Typography component="span" sx={{ fontSize: '0.9rem', color: COLORS.text.muted }}>
                      {TEXT.TIERS.PER_MONTH}
                    </Typography>
                  )}
                </Box>

                {/* Features */}
                <List dense sx={{ mb: 3, textAlign: 'left' }}>
                  {cfg.features.map((f, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 0.3 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Check sx={{ fontSize: 16, color: tc.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={f}
                        primaryTypographyProps={{ fontSize: '0.8rem', color: COLORS.text.secondary }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* CTA */}
                <Button
                  fullWidth
                  variant={isCurrent ? 'outlined' : 'contained'}
                  disabled={isCurrent}
                  onClick={() => setTier(key)}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 2,
                    ...(isCurrent
                      ? {
                          borderColor: tc.border,
                          color: tc.primary,
                        }
                      : {
                          background: featured
                            ? `linear-gradient(135deg, ${COLORS.gold.primary}, ${COLORS.gold.dark})`
                            : tc.primary,
                          color: '#000',
                          '&:hover': {
                            background: featured
                              ? `linear-gradient(135deg, ${COLORS.gold.light}, ${COLORS.gold.primary})`
                              : tc.primary,
                            filter: 'brightness(1.1)',
                          },
                        }),
                  }}
                >
                  {isCurrent ? TEXT.TIERS.CURRENT : `${TEXT.TIERS.UPGRADE} to ${cfg.name}`}
                </Button>
              </TierCard>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}
